// Iter 106 — Google-Docs style text-colour swatch picker.
// Replaces the native <input type=color> with a popover containing a
// fixed Google-Docs colour palette (10 cols × 8 rows). Click a swatch
// → emit hex; click "Reset" → emit null (caller calls unsetColor).
// A small "Custom…" row hosts the native colour input for users who
// need an arbitrary hex.

import { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Palette, Check } from "lucide-react";

// Google Docs / Slides standard text-colour palette.
// Row 1: greyscale (black → white).
// Row 2: vivid hues.
// Rows 3-7: progressively darker tints of row 2.
const PALETTE = [
  // Greyscale
  ["#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff"],
  // Vivid hues
  ["#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff"],
  // Lightest tint
  ["#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc"],
  // Light
  ["#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd"],
  // Mid
  ["#cc4125", "#e06666", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0"],
  // Mid-dark
  ["#a61c00", "#cc0000", "#e69138", "#f1c232", "#6aa84f", "#45818e", "#3c78d8", "#3d85c6", "#674ea7", "#a64d79"],
  // Dark
  ["#85200c", "#990000", "#b45f06", "#bf9000", "#38761d", "#134f5c", "#1155cc", "#0b5394", "#351c75", "#741b47"],
  // Darkest
  ["#5b0f00", "#660000", "#783f04", "#7f6000", "#274e13", "#0c343d", "#1c4587", "#073763", "#20124d", "#4c1130"],
];

export default function ColorPalettePopover({
  value = "",
  onPick,        // (hex: string) => void — apply colour
  onReset,       // () => void — clear colour
  title = "Text colour",
  testId = "composer-color-trigger",
  triggerClassName = "p-1.5 rounded text-[13px] text-slate-600 hover:bg-slate-100",
}) {
  const [open, setOpen] = useState(false);
  const customRef = useRef(null);

  const handlePick = (hex) => {
    setOpen(false);
    // Treat black as "reset" so users can clear the inline style cleanly,
    // matching the prior behaviour. They can still pick deep blacks via
    // the Custom… input if they really need #000.
    if (hex.toLowerCase() === "#000000") {
      onReset?.();
    } else {
      onPick?.(hex);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title}
          data-testid={testId}
          className={`${triggerClassName} relative inline-flex items-center justify-center h-7 w-7`}
          // Prevent the contenteditable from losing selection when the
          // popover opens. TipTap keeps the selection in editor.state so
          // chain().focus() restores it on apply.
          onMouseDown={(e) => e.preventDefault()}
        >
          <Palette className="h-3.5 w-3.5" />
          {/* Tiny colour bar showing current selection (Google parity). */}
          <span
            aria-hidden="true"
            className="absolute bottom-0.5 left-1 right-1 h-[3px] rounded-sm"
            style={{ backgroundColor: value || "#000000" }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={6}
        className="w-auto p-3 z-[100]"
        data-testid="composer-color-popover"
        // Same selection-preservation trick — don't let the popover
        // steal focus from the editor when its content mounts.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="text-[12px] font-medium text-slate-700 mb-2">Text colour</div>
        <div
          role="grid"
          aria-label="Text colour palette"
          className="grid grid-cols-10 gap-1"
        >
          {PALETTE.flatMap((row, ri) =>
            row.map((hex) => {
              const isActive = (value || "").toLowerCase() === hex.toLowerCase();
              return (
                <button
                  key={`${ri}-${hex}`}
                  type="button"
                  role="gridcell"
                  title={hex}
                  data-testid={`composer-color-swatch-${hex.slice(1)}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(hex)}
                  className={`relative h-5 w-5 rounded-[3px] border ${
                    hex === "#ffffff" ? "border-slate-300" : "border-black/10"
                  } hover:scale-110 transition-transform`}
                  style={{ backgroundColor: hex }}
                >
                  {isActive && (
                    <Check
                      className={`absolute inset-0 m-auto h-3 w-3 ${
                        ["#ffffff", "#f3f3f3", "#efefef", "#d9d9d9", "#cccccc", "#b7b7b7", "#fff2cc", "#ffe599", "#fce5cd", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc", "#f4cccc"].includes(hex.toLowerCase())
                          ? "text-slate-900"
                          : "text-white"
                      }`}
                      strokeWidth={3}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Custom + Reset row */}
        <div className="mt-3 pt-2 border-t border-slate-200 flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="composer-color-custom"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => customRef.current?.click()}
            className="text-[12px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
          >
            <span
              className="inline-block h-3 w-3 rounded-sm border border-slate-300"
              style={{
                background:
                  "conic-gradient(from 180deg, red, orange, yellow, green, cyan, blue, magenta, red)",
              }}
            />
            Custom…
          </button>
          <input
            ref={customRef}
            type="color"
            data-testid="composer-color-custom-input"
            // Lightweight: native input lives off-screen so the user
            // taps "Custom…" to invoke the OS picker. Width 0 so it
            // never overflows or intercepts neighbouring controls.
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            onChange={(e) => {
              const c = e.target.value;
              setOpen(false);
              if (c.toLowerCase() === "#000000") onReset?.();
              else onPick?.(c);
            }}
          />
          <button
            type="button"
            data-testid="composer-color-reset"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setOpen(false); onReset?.(); }}
            className="text-[12px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
          >
            Reset
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
